import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Archive, Ban, PackageCheck, AlertTriangle, ScanLine, ShieldCheck, FlaskConical } from "lucide-react";
import { Wrap } from "@/components/Content";
import { Seo, breadcrumbSchema } from "@/components/Seo";
import { CARE_LAB_TOPICS, ARTICLES } from "@/data/articles";
import { ASSETS } from "@/lib/api";

const ICONS = { archive: Archive, ban: Ban, package: PackageCheck, alert: AlertTriangle, scan: ScanLine, shield: ShieldCheck };

export default function CardCareLab() {
  return (
    <div className="bg-ink min-h-screen pb-24">
      <Seo
        title="The Card Care Lab | Trading Card Storage, Protection & Grading Prep Guides"
        description="Know what touches your cards. The CardScience HQ Card Care Lab teaches how to store, protect and handle your collection — and what can permanently damage it. Six educational guides."
        path="/card-care-lab"
        jsonLd={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Card Care Lab", path: "/card-care-lab" },
        ])}
      />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[color:var(--line)]">
        <div className="absolute inset-0">
          <img src={ASSETS.careLabHero} alt="" aria-hidden className="h-full w-full object-cover object-center opacity-[0.16]" />
          <div className="absolute inset-0" style={{ background: "radial-gradient(70% 70% at 50% 0%, rgba(36,224,255,0.18), transparent 60%), linear-gradient(180deg, rgba(6,6,8,0.55), #060608 88%)" }} />
        </div>
        <div className="relative mx-auto max-w-[1000px] px-5 py-20 text-center">
          <p className="font-tech text-[11px] tracking-[0.4em] text-[color:var(--chrome-3)] inline-flex items-center gap-2 justify-center">
            <FlaskConical size={14} className="text-[color:var(--cyan)]" /> CARDSCIENCE HQ · CARD CARE LAB
          </p>
          <h1 className="h-display text-4xl sm:text-5xl lg:text-6xl text-chrome leading-[1.03] mt-5">
            KNOW WHAT <span className="text-holo">TOUCHES</span> YOUR CARDS.
          </h1>
          <p className="text-[color:var(--chrome-2)] mt-5 max-w-2xl mx-auto leading-relaxed">
            Proper card care starts long before a card reaches our lab. Learn how to handle, protect and store your
            collection — and what can permanently damage it.
          </p>
          <Link to="/submit" data-testid="care-lab-hero-cta" className="btn-holo inline-flex items-center gap-2 rounded-xl px-8 py-4 text-sm mt-9">
            ENTER THE CARD CARE LAB <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Wrap className="py-16 max-w-[1100px]">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {CARE_LAB_TOPICS.map((t) => {
            const Icon = ICONS[t.iconKey] || FlaskConical;
            const art = ARTICLES[t.slug];
            return (
              <Link
                key={t.slug}
                to={art?.path || `/library/${t.slug}`}
                data-testid={`care-lab-topic-${t.slug}`}
                className="group relative rounded-2xl border border-[color:var(--line)] glass p-6 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:border-[color:var(--cyan)]/60"
              >
                <div className="flex items-center justify-between">
                  <div className="h-11 w-11 rounded-xl holo-border flex items-center justify-center text-[color:var(--cyan)]">
                    <Icon size={20} />
                  </div>
                  <span className="font-display font-black text-2xl text-[color:var(--line-2)] group-hover:text-holo transition-colors">{t.n}</span>
                </div>
                <h2 className="h-display text-xl text-chrome leading-tight mt-5 group-hover:text-white transition-colors">{t.title}</h2>
                <p className="text-sm text-[color:var(--chrome-2)] leading-relaxed mt-2.5 flex-1">{t.copy}</p>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-tech tracking-widest text-[color:var(--cyan)] mt-5 group-hover:gap-2.5 transition-all">
                  READ THE GUIDE <ArrowRight size={13} />
                </span>
              </Link>
            );
          })}
        </div>

        <div className="mt-14 rounded-3xl holo-border holo-glow p-8 sm:p-10 text-center">
          <FlaskConical size={30} className="text-holo mx-auto mb-4" />
          <h2 className="h-display text-2xl sm:text-3xl text-chrome">CARE FOR THE CARD. PROTECT THE COLLECTION.</h2>
          <p className="text-[color:var(--chrome-2)] mt-3 max-w-xl mx-auto leading-relaxed">
            When a card needs more than good storage, our lab evaluates every submission individually — honestly, and never with a guaranteed outcome.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <Link to="/submit" className="btn-holo inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm">SUBMIT A COLLECTIBLE <ArrowRight size={16} /></Link>
            <Link to="/library" className="btn-ghost inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm">THE LIBRARY</Link>
          </div>
        </div>
      </Wrap>
    </div>
  );
}
